import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import ClusteredMap from "@/components/ClusteredMap";
import PageGuide from "@/components/PageGuide";
import { ShieldWarning } from "@phosphor-icons/react";

const CATEGORY_COLORS = {
  mammals: "#92400E", birds: "#2563EB", insects: "#D97706", reptiles: "#65A30D",
  amphibians: "#059669", fish: "#0284C7", aquatic: "#0284C7", fungi: "#B45309", other: "#6B7280",
};

const GROUPS = [
  { slug: "", label: "All" },
  { slug: "mammals", label: "Mammals" },
  { slug: "birds", label: "Birds" },
  { slug: "insects", label: "Insects" },
  { slug: "reptiles", label: "Reptiles" },
  { slug: "amphibians", label: "Amphibians" },
  { slug: "fish-marine", label: "Fish & Marine" },
  { slug: "fungi", label: "Fungi" },
];

function popupHtml(m) {
  return `<div style="font-family:sans-serif;min-width:170px">
    <b>${m.title || "Species observation"}</b><br/>
    <span style="color:#6B7280;font-size:11px">${m.category || ""} · ${m.date || ""}${m.place ? " · " + m.place : ""}${m.obscured ? " · location obscured" : ""}</span><br/>
    <a href="/observations/${m.id}" style="color:#166534;font-size:12px">Open observation →</a>
  </div>`;
}

/** SpeciesPulse biodiversity map — clustered observation markers with
 * privacy-safe (approximate/obscured) coordinates only. */
export default function SpeciesMapPage() {
  const [group, setGroup] = useState("");
  const [markers, setMarkers] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    setMarkers(null);
    http.get("/speciespulse/map", { params: group ? { group } : {} })
      .then(({ data }) => { setMarkers(data.markers || []); setNote(data.obscuration_note || ""); })
      .catch(() => setMarkers([]));
  }, [group]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <main className="min-h-screen bg-white text-[#14532D] pt-32 md:pt-36 pb-24" data-testid="speciespulse-map">
      <SeoMeta
        title="SpeciesPulse Map — where the world's species are being observed"
        description="The living biodiversity map: community species observations clustered worldwide. Coordinates are always approximate; threatened-species locations are obscured."
        canonical={`${origin}/speciespulse/map`}
      />
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "SpeciesPulse", to: "/speciespulse" }, { label: "Map" }]} />
        <div className="mt-4"><PageGuide pageKey="species:map" testidPrefix="speciespulse-map" /></div>
        <div className="mt-4 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3F6212]">Biodiversity map</div>
        <h1 className="mt-1 font-serif-display text-4xl md:text-5xl text-[#166534]" data-testid="speciespulse-map-title">
          Where life is being seen.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-[#4B5563]">
          Community species observations, clustered worldwide. Zoom in to explore; every marker links to
          its observation record.
        </p>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="speciespulse-map-filters">
          {GROUPS.map((g) => (
            <button key={g.slug} onClick={() => setGroup(g.slug)} data-testid={`speciespulse-map-filter-${g.slug || "all"}`}
              className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                group === g.slug ? "bg-[#166534] text-white" : "border border-[#166534]/25 text-[#166534] hover:bg-[#F0FDF4]"}`}>
              {g.label}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {markers === null ? (
            <div className="h-[440px] rounded-3xl bg-[#F0FDF4] animate-pulse" />
          ) : (
            <ClusteredMap markers={markers} colorFor={(m) => CATEGORY_COLORS[m.category] || "#166534"}
              popupHtml={popupHtml} testid="speciespulse-worldmap" height={480} />
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <span className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#6B7280]" data-testid="speciespulse-map-count">
            {markers ? `${markers.length} observation${markers.length === 1 ? "" : "s"} plotted` : "Loading…"}
          </span>
          {note && (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-[#6B7280]" data-testid="speciespulse-map-note">
              <ShieldWarning size={12} weight="fill" className="text-[#B45309]" /> {note}
            </span>
          )}
        </div>

        {markers && markers.length === 0 && (
          <p className="mt-6 rounded-2xl border border-dashed border-[#166534]/25 bg-[#F0FDF4]/50 p-6 text-sm text-[#4B5563]" data-testid="speciespulse-map-empty">
            No observations with a shared location yet. Photograph a species, identify it and share an
            approximate location to put the first pin on this map.
          </p>
        )}

        <div className="mt-8">
          <Link to="/identify" data-testid="speciespulse-map-identify"
            className="inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white hover:bg-[#14532D]">
            Add an observation
          </Link>
        </div>
      </div>
    </main>
  );
}
